"use client";
import { memo, useMemo, useState } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import {
  Check,
  Minus,
  Plus,
  Sparkles,
  ArrowRight,
  ShieldCheck,
  Users,
  CreditCard,
  Clock,
} from "lucide-react";
import finalImage from "../../assets/pricing.jpg";
import CallToAction from "../CallToAction";
import Seo, { SITE_URL } from "../../WebsiteComponents/Seo";
import { ROUTES } from "../../seoData";

const PATH = "/pricing";
const TITLE = ROUTES[PATH].title;
const DESCRIPTION = ROUTES[PATH].description;

const PLAN = {
  id: "standard",
  name: "Standard",
  tagline: "Everything your team needs to capture, manage, and convert leads.",
  price: {
    monthly: 499, // ₹ per user / month (billed monthly)
    annual: 399, // ₹ per user / month (billed annually)
  },
  features: [
    "Per-user pricing — pay only for active team members",
    "Centralized lead dashboard",
    "Lead capture forms & manual entry",
    "Assign leads to team members",
    "Activity timeline (calls, notes, updates)",
    "Automated lead assignment",
    "Follow-up reminders & notifications",
    "Lead source tracking",
    "Reports & performance analytics",
    "WhatsApp automation included",
    "Priority email support",
  ],
};

const TRUST_POINTS = [
  { icon: Users, label: "Per-user pricing" },
  { icon: Clock, label: "14-day free trial" },
  { icon: CreditCard, label: "No credit card required" },
];

const FAQS = [
  {
    q: "How does monthly vs. annual billing work?",
    a: "Monthly billing is ₹499 per user per month. Annual billing is ₹399 per user per month — a 20% saving — billed once for the full year, per user.",
  },
  {
    q: "What does 'per user' mean?",
    a: "You pay for each active team member who has a seat in LMSBOTS. You can add or remove users at any time, and your bill adjusts on the next cycle.",
  },
  {
    q: "How does the free trial work?",
    a: "You get full access to LMSBOTS for 14 days with all features enabled. No credit card required to start.",
  },
  {
    q: "Are there extra charges for WhatsApp messages?",
    a: "We follow Meta's official conversation-based pricing. LMSBOTS adds no hidden markup — you only pay standard WhatsApp charges.",
  },
  {
    q: "Can I cancel at any time?",
    a: "Yes. You can cancel your plan at any time, and your account stays active until the end of the current billing period.",
  },
];

const JSON_LD = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${SITE_URL}${PATH}#webpage`,
    url: `${SITE_URL}${PATH}`,
    name: TITLE,
    description: DESCRIPTION,
    inLanguage: "en-IN",
    isPartOf: { "@id": `${SITE_URL}/#website` },
    about: { "@id": `${SITE_URL}/#organization` },
    primaryImageOfPage: { "@type": "ImageObject", url: `${SITE_URL}/og-image.png` },
    breadcrumb: { "@id": `${SITE_URL}${PATH}#breadcrumb` },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": `${SITE_URL}${PATH}#breadcrumb`,
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
      { "@type": "ListItem", position: 2, name: "Pricing", item: `${SITE_URL}${PATH}` },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "LMSBOTS CRM Software",
    description: DESCRIPTION,
    brand: { "@type": "Brand", name: "LMSBOTS" },
    url: `${SITE_URL}${PATH}`,
    image: [`${SITE_URL}/og-image.png`],
    offers: [
      {
        "@type": "Offer",
        name: "Standard — Monthly (per user)",
        priceCurrency: "INR",
        price: "499",
        priceSpecification: {
          "@type": "UnitPriceSpecification",
          price: "499",
          priceCurrency: "INR",
          unitText: "MONTH",
          referenceQuantity: { "@type": "QuantitativeValue", value: 1, unitText: "user" },
        },
        availability: "https://schema.org/InStock",
        url: `${SITE_URL}${PATH}`,
      },
      {
        "@type": "Offer",
        name: "Standard — Annual (per user)",
        priceCurrency: "INR",
        price: "399",
        priceSpecification: {
          "@type": "UnitPriceSpecification",
          price: "399",
          priceCurrency: "INR",
          unitText: "MONTH",
          referenceQuantity: { "@type": "QuantitativeValue", value: 1, unitText: "user" },
        },
        availability: "https://schema.org/InStock",
        url: `${SITE_URL}${PATH}`,
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  },
];

// Memoised card body — only the price changes when the toggle is switched,
// so this keeps re-renders cheap and makes future feature-list updates safe.
const PriceBlock = memo(({ perUser, billingPeriod, note }) => (
  <div className="mt-8">
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
      <span className="text-[40px] font-light leading-none tracking-tight text-[#28194f] sm:text-[48px] lg:text-[54px]">
        ₹{perUser.toLocaleString()}
      </span>
      <span className="text-sm font-medium text-slate-500">/user/mo</span>
      {billingPeriod === "annual" && (
        <span className="ml-1 rounded-full bg-[#f1ecfa] px-2.5 py-1 text-[11px] font-semibold text-[#48247f]">
          Save 20% vs ₹499
        </span>
      )}
    </div>
    <p className="mt-2 text-xs font-medium text-[#0f9d86]">{note}</p>
  </div>
));
PriceBlock.displayName = "PriceBlock";

const FAQItem = ({ faq, open, onToggle }) => (
  <details
    open={open}
    onToggle={onToggle}
    className="group overflow-hidden rounded-2xl border border-[#48247f]/10 bg-white shadow-[0_8px_30px_-14px_rgba(72,36,127,0.22)] transition-colors open:border-[#702cb0]/40"
  >
    <summary className="flex min-h-[56px] cursor-pointer list-none items-start justify-between gap-3 px-4 py-4 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#702cb0] sm:min-h-[64px] sm:items-center sm:gap-4 sm:px-6 [&::-webkit-details-marker]:hidden">
      <h3 className="flex-1 text-[15px] font-medium leading-snug text-[#28194f] sm:text-base">
        {faq.q}
      </h3>
      <span
        className={`mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full transition-all duration-300 sm:mt-0 ${
          open ? "rotate-180 bg-[#12dfbd] text-[#102b37]" : "bg-[#f1ecfa] text-[#48247f]"
        }`}
        aria-hidden="true"
      >
        {open ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
      </span>
    </summary>
    <p className="px-4 pb-5 text-sm leading-[1.75] text-[#3b2a63] sm:px-6 sm:pb-6 sm:text-[15px]">
      {faq.a}
    </p>
  </details>
);

const Pricing = () => {
  const [billingPeriod, setBillingPeriod] = useState("monthly");
  const [openFAQ, setOpenFAQ] = useState(0);

  const prefersReduced = useReducedMotion();
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], [0, -40]);

  const perUser = billingPeriod === "monthly" ? PLAN.price.monthly : PLAN.price.annual;
  const note =
    billingPeriod === "monthly"
      ? "Per user · billed monthly · cancel anytime"
      : "Per user · billed annually · save 20% vs monthly";

  const ctaPrice = useMemo(() => `₹${perUser.toLocaleString()}`, [perUser]);
  const imageStyle = prefersReduced ? {} : { y };

  return (
    <>
      <Seo title={TITLE} description={DESCRIPTION} path={PATH} jsonLd={JSON_LD} />

      <div
        className="relative w-full overflow-x-clip bg-white font-sans text-[#28194f] antialiased"
      >
        {/* ================= HERO ================= */}
        <section
          aria-labelledby="pricing-heading"
          className="relative isolate w-full overflow-hidden bg-gradient-to-b from-[#fbf7ff] via-white to-white"
        >
          <div className="pointer-events-none absolute -right-24 -top-24 -z-10 h-72 w-72 rounded-full bg-[#702cb0]/10 blur-3xl sm:h-96 sm:w-96" />
          <div className="pointer-events-none absolute -left-24 bottom-0 -z-10 h-64 w-64 rounded-full bg-[#12dfbd]/12 blur-3xl sm:h-80 sm:w-80" />
          <div
            className="pointer-events-none absolute inset-0 -z-10 opacity-40"
            style={{
              backgroundImage: "radial-gradient(#48247f18 1px, transparent 1px)",
              backgroundSize: "22px 22px",
              WebkitMaskImage: "radial-gradient(ellipse at 40% 40%, black 30%, transparent 80%)",
              maskImage: "radial-gradient(ellipse at 40% 40%, black 30%, transparent 80%)",
            }}
          />

          <div className="relative z-10 mx-auto grid w-full max-w-[1500px] items-center gap-10 px-4 pb-14 pt-20 sm:gap-12 sm:px-8 sm:pb-16 sm:pt-24 md:px-10 lg:grid-cols-[1.05fr_1fr] lg:gap-14 lg:px-14 lg:pb-24 lg:pt-32 xl:px-20">
            <div className="mx-auto max-w-xl text-center lg:mx-0 lg:text-left">
              <motion.div
                initial={prefersReduced ? false : { opacity: 0, y: -12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#48247f]/15 bg-white px-3 py-1 text-[11px] font-medium text-[#48247f] shadow-sm sm:mb-6 sm:text-xs"
              >
                <Sparkles className="h-3.5 w-3.5 text-[#702cb0]" />
                LMSBOTS Pricing
              </motion.div>

              <motion.h1
                id="pricing-heading"
                initial={prefersReduced ? false : { opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.6 }}
                className="text-balance text-[26px] font-light leading-[1.2] tracking-tight sm:text-[32px] md:text-[36px] lg:text-[40px] xl:text-[44px]"
              >
                One simple plan to{" "}
                <span className="bg-gradient-to-r from-[#48247f] to-[#702cb0] bg-clip-text font-normal text-transparent">
                  capture & convert more leads.
                </span>
              </motion.h1>

              <motion.p
                initial={prefersReduced ? false : { opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="mx-auto mt-4 max-w-[560px] text-sm font-light leading-[1.75] text-[#3b2a63] sm:text-base lg:mx-0 lg:text-[17px]"
              >
                Everything you need to manage, automate, and close leads on WhatsApp — priced per user,
                so your bill scales with your team. No tiers, no hidden fees.
              </motion.p>

              {/* Billing toggle + chip (wraps on very small screens) */}
              <motion.div
                initial={prefersReduced ? false : { opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="mt-7 flex flex-wrap items-center justify-center gap-3 sm:mt-8 lg:justify-start"
              >
                <div
                  role="group"
                  aria-label="Billing period"
                  className="inline-flex rounded-xl border border-[#48247f]/10 bg-white p-1 shadow-sm"
                >
                  {[
                    { id: "monthly", label: "Monthly" },
                    { id: "annual", label: "Annual" },
                  ].map((b) => {
                    const active = billingPeriod === b.id;
                    return (
                      <button
                        key={b.id}
                        type="button"
                        onClick={() => setBillingPeriod(b.id)}
                        aria-pressed={active}
                        className={`inline-flex min-h-[40px] items-center justify-center rounded-lg px-3.5 text-xs font-semibold transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#702cb0] focus-visible:ring-offset-2 sm:px-4 sm:text-sm ${
                          active
                            ? "bg-gradient-to-r from-[#48247f] to-[#702cb0] text-white shadow-md shadow-[#48247f]/20"
                            : "text-[#3b2a63] hover:text-[#48247f]"
                        }`}
                      >
                        {b.label}
                      </button>
                    );
                  })}
                </div>
                <span className="inline-flex items-center rounded-full bg-[#e3faf5] px-2.5 py-1 text-[11px] font-semibold text-[#0f9d86]">
                  Save 20% annually
                </span>
              </motion.div>

              <motion.ul
                initial={prefersReduced ? false : { opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4, duration: 0.6 }}
                className="mt-7 flex flex-wrap items-center justify-center gap-x-4 gap-y-2.5 sm:mt-8 sm:gap-x-5 lg:justify-start"
              >
                {TRUST_POINTS.map(({ icon: Icon, label }) => (
                  <li
                    key={label}
                    className="inline-flex items-center gap-2 text-[11px] font-medium text-[#3b2a63] sm:text-xs md:text-sm"
                  >
                    <Icon className="h-4 w-4 text-[#0f9d86]" strokeWidth={2.4} />
                    {label}
                  </li>
                ))}
              </motion.ul>
            </div>

            <motion.div
              style={imageStyle}
              initial={prefersReduced ? false : { opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              className="relative mx-auto hidden w-full max-w-[600px] px-3 pb-6 pt-3 lg:block lg:max-w-none"
            >
              <div className="absolute inset-x-8 bottom-0 top-10 rounded-3xl bg-[#e3faf5]" />
              <div className="relative rounded-2xl bg-white p-2 shadow-xl ring-1 ring-slate-200">
                <img
                  src={finalImage}
                  alt="LMSBOTS pricing and lead management dashboard"
                  width="1200"
                  height="800"
                  fetchPriority="high"
                  decoding="async"
                  className="h-auto w-full rounded-xl border border-slate-200 object-cover"
                />
              </div>
            </motion.div>
          </div>
        </section>

        {/* ================= SINGLE PRICING CARD ================= */}
        <section
          aria-labelledby="plan-heading"
          className="relative w-full bg-white py-12 sm:py-16 lg:py-24"
        >
          <div className="mx-auto w-full max-w-[1300px] px-4 sm:px-8 md:px-10 lg:px-14">
            <motion.div
              initial={prefersReduced ? false : { opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              className="mx-auto mb-8 max-w-2xl text-center sm:mb-10 lg:mb-14"
            >
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-[#48247f]/10 bg-white px-3 py-1 text-[11px] font-medium text-[#48247f] shadow-sm sm:text-xs">
                <ShieldCheck className="h-3.5 w-3.5 text-[#702cb0]" />
                Simple, transparent pricing
              </div>
              <h2
                id="plan-heading"
                className="text-balance text-[24px] font-light leading-[1.25] tracking-tight sm:text-[30px] md:text-[34px] lg:text-[38px]"
              >
                One plan,{" "}
                <span className="bg-gradient-to-r from-[#48247f] to-[#702cb0] bg-clip-text font-normal text-transparent">
                  priced per user
                </span>
              </h2>
              <p className="mx-auto mt-4 max-w-[560px] text-sm leading-[1.7] text-[#3b2a63] sm:text-[15px] md:text-base">
                Pick the billing option that fits — monthly for flexibility, annual for a lower per-user
                rate.
              </p>
            </motion.div>

            <motion.article
              aria-labelledby="plan-heading"
              itemScope
              itemType="https://schema.org/Product"
              initial={prefersReduced ? false : { opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
              className="group relative mx-auto max-w-2xl overflow-hidden rounded-2xl border border-[#702cb0]/20 bg-white p-5 shadow-[0_24px_60px_-24px_rgba(72,36,127,0.4)] ring-2 ring-[#702cb0]/20 sm:p-8 lg:p-10"
            >
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#12dfbd] via-[#702cb0] to-[#48247f]" />

              <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-[#48247f] to-[#702cb0] text-white shadow-md shadow-[#48247f]/25 sm:h-12 sm:w-12">
                    <Sparkles className="h-5 w-5" />
                  </span>
                  <div>
                    <h3
                      className="text-xl font-medium tracking-tight text-[#28194f] sm:text-2xl"
                      itemProp="name"
                    >
                      {PLAN.name}
                    </h3>
                    <p className="text-sm text-[#3b2a63]" itemProp="description">
                      {PLAN.tagline}
                    </p>
                  </div>
                </div>

                <span className="inline-flex w-fit items-center rounded-full bg-[#e3faf5] px-3 py-1 text-[11px] font-semibold text-[#0f9d86]">
                  Best value
                </span>
              </div>

              <PriceBlock perUser={perUser} billingPeriod={billingPeriod} note={note} />

              <div className="my-6 h-px w-full bg-[#48247f]/10 sm:my-8" />

              <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#702cb0]/70">
                What's included
              </p>

              <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {PLAN.features.map((feature) => (
                  <li
                    key={feature}
                    className="flex items-start gap-3 text-sm leading-[1.6] text-[#28194f]"
                  >
                    <span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[#f1ecfa] text-[#48247f]">
                      <Check className="h-3 w-3" strokeWidth={3} />
                    </span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-7 sm:mt-8">
                <a
                  href={`/contact?plan=${PLAN.id}&billing=${billingPeriod}`}
                  className="group/btn inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#48247f] to-[#702cb0] px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-[#48247f]/25 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[#48247f]/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#48247f] focus-visible:ring-offset-2 sm:px-6"
                >
                  Get Started — {ctaPrice} /user/mo
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover/btn:translate-x-1" />
                </a>
                <p className="mt-3 text-center text-[11px] text-slate-500 sm:text-xs">
                  14-day free trial · No credit card required · Add or remove users anytime
                </p>
              </div>
            </motion.article>
          </div>
        </section>

        {/* ================= FAQ ================= */}
        <section
          aria-labelledby="faq-heading"
          className="relative isolate w-full overflow-hidden bg-[#f6f3fc] py-12 sm:py-16 lg:py-24"
        >
          <div className="pointer-events-none absolute -bottom-24 -right-24 -z-10 h-72 w-72 rounded-full bg-[#12dfbd]/15 blur-3xl sm:h-96 sm:w-96" />
          <div className="mx-auto w-full max-w-[900px] px-4 sm:px-8 md:px-10 lg:px-14">
            <motion.div
              initial={prefersReduced ? false : { opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mx-auto mb-8 max-w-2xl text-center sm:mb-10 lg:mb-14"
            >
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-[#48247f]/10 bg-white px-3 py-1 text-[11px] font-medium text-[#48247f] shadow-sm sm:text-xs">
                <Sparkles className="h-3.5 w-3.5 text-[#702cb0]" />
                FAQ
              </div>
              <h2
                id="faq-heading"
                className="text-balance text-[24px] font-light leading-[1.25] tracking-tight sm:text-[30px] md:text-[34px] lg:text-[38px]"
              >
                Common{" "}
                <span className="bg-gradient-to-r from-[#48247f] to-[#702cb0] bg-clip-text font-normal text-transparent">
                  questions
                </span>
              </h2>
            </motion.div>

            <div className="space-y-3 sm:space-y-4">
              {FAQS.map((faq, i) => (
                <FAQItem
                  key={faq.q}
                  faq={faq}
                  open={openFAQ === i}
                  onToggle={(e) => {
                    if (e.currentTarget.open) setOpenFAQ(i);
                    else if (openFAQ === i) setOpenFAQ(null);
                  }}
                />
              ))}
            </div>
          </div>
        </section>

        <CallToAction />
      </div>
    </>
  );
};

export default Pricing;